/** Cookie key：广告来源（Proxy 门禁 + 客户端分流共用） */
export const AD_SOURCE_COOKIE_KEY = "edgen_ad_source";

/**
 * 命中即跳过 onboarding 直落 Brief 的广告来源集合（统一大写；SAVING_ONBOARDING_SKIP 同 SAVING
 * 承接但额外跳过 onboarding；ROAST_HANDOFF 同 ALPHA_PICK/PORTFOLIO_CHECK 直落 Home）
 *
 * ⚠️ **`AI_JOB_RISK` 不得加进来** —— 它是「走另一套 onboarding」，不是「跳过 onboarding」。
 * 该来源走 9 题问诊 + AI 冲击结果页，归 `adSourceToScene()` 分流，两件事不要混。
 * 回归断言钉在 `__tests__/adSourceToScene.test.ts`。
 */
export const AD_SKIP_SOURCES = new Set([
  "ALPHA_PICK",
  "PORTFOLIO_CHECK",
  "SAVING_ONBOARDING_SKIP",
  "ROAST_HANDOFF",
]);

/** 判定某 ad_source 值是否应跳过 onboarding（纯函数，Proxy/客户端共用；统一大写比较） */
export function isAdSkipSource(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return AD_SKIP_SOURCES.has(value.toUpperCase());
}

/**
 * URL 查询参数名 —— 投放落地到 **web** 时的 ad_source 入口。
 *
 * 为什么需要它：Adjust 链接（`edgen.go.link` / `edwealth.go.link` / `wbsy.adj.st`）在用户
 * **没装 App** 时会 fallback 到 web。此前 web 侧只有两个 cookie 写入点 —— 原生 bridge 深链
 * （`lib/bridge/handleDeepLinkOpened.ts`）与登录后的后端回填（`useOnboardingEntryGate`）——
 * 二者都要求用户已经在 App 里。于是 fallback 到 web 的那批人 `ad_source` 直接丢失，
 * 被当自然流量走默认 onboarding，广告承接在 web 上整条失效。
 */
export const AD_SOURCE_QUERY_KEY = "ad_source";

/** `ad_source` 允许的字符集 —— 该值会写进 cookie 并作为 `OS-Ad-Source` 头发给后端。 */
const AD_SOURCE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * 校验并规范化来自 URL 的 ad_source；非法返回 null。
 *
 * **不做枚举白名单**：ad_source 的全集由投放侧定义、随时新增，前端硬编码一份必然过期
 * （`AD_SKIP_SOURCES` 只是「跳过 onboarding」那个子集，不是全集）。这里只保证它是个
 * 安全的标识符 —— 未知值经 `adSourceToScene()` 自然落回 `"default"`。
 *
 * 大写化与 {@link import("./adSourceCookie.client").setAdSourceCookie} 保持一致，
 * 否则同一来源会因大小写在 cookie 里存成两种形态。
 */
export function sanitizeAdSourceParam(
  raw: string | null | undefined,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed || !AD_SOURCE_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed.toUpperCase();
}

/** 从原生深链 payload 提取 ad_source（businessQuery.ad_source）。 */
export function extractAdSourceFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  const raw = data?.businessQuery?.ad_source?.trim();
  return raw ? raw : null;
}

/** 从原生深链 payload 提取 query_source（businessQuery.query_source，兴趣分类广告来源）。 */
export function extractQuerySourceFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  const raw = data?.businessQuery?.query_source?.trim();
  return raw ? raw : null;
}

/**
 * 从原生深链 payload 提取 landing_id（businessQuery.landing_id，主题榜单广告承接素材主键）。
 * 仅 trim()，大小写保真 —— 后端按主键精确匹配并自行兼容大小写，不沿用 ad_source 的大写化。
 */
export function extractLandingIdFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  const raw = data?.businessQuery?.landing_id?.trim();
  return raw ? raw : null;
}
